import React, { useState, useEffect } from 'react';
import { contentAPI, matchesAPI } from '../../services/api';
import { FaUserTie, FaFlag, FaEye, FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const DesignationsSection = () => {
  const { language, t } = useLanguage();
  const [designations, setDesignations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMatchFilter, setSelectedMatchFilter] = useState('all');
  const [designationMatchFilter, setDesignationMatchFilter] = useState('');
  const [designationRoundFilter, setDesignationRoundFilter] = useState('');
  const [designationGroupFilter, setDesignationGroupFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [designationsRes, matchesRes] = await Promise.all([
        contentAPI.getDesignations(),
        matchesAPI.getAll()
      ]);
      setDesignations(designationsRes.data || []);
      setMatches(matchesRes.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setDesignations([]);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await contentAPI.updateDesignation(formData.id, formData);
        alert('Désignation modifiée avec succès!');
      } else {
        await contentAPI.createDesignation(formData);
        alert('Désignation créée avec succès!');
      }
      loadData();
      setFormData({});
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette désignation?')) {
      try {
        await contentAPI.deleteDesignation(id);
        loadData();
        alert('Désignation supprimée!');
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleEdit = (designation) => {
    setFormData(designation);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getMatchInfo = (matchId) => {
    const match = matches.find(m => m.id === parseInt(matchId));
    if (!match) return 'Match inconnu';
    const team1 = language === 'ar' ? (match.home_team_name_ar || match.home_team_name) : match.home_team_name;
    const team2 = language === 'ar' ? (match.away_team_name_ar || match.away_team_name) : match.away_team_name;
    return `${team1} vs ${team2} - J${match.round}`;
  };

  const filteredDesignations = designations.filter(d => {
    const match = matches.find(m => m.id === d.match_id);
    if (!match) return false;

    if (selectedMatchFilter !== 'all' && d.match_id !== parseInt(selectedMatchFilter)) return false;

    if (designationRoundFilter && String(match.round) !== designationRoundFilter) return false;

    if (designationGroupFilter) {
      if (String(match.home_team_group) !== designationGroupFilter && String(match.away_team_group) !== designationGroupFilter) return false;
    }

    if (designationMatchFilter) {
      const q = designationMatchFilter.toLowerCase();
      const h = (match.home_team_name || '').toLowerCase();
      const a = (match.away_team_name || '').toLowerCase();
      if (!h.includes(q) && !a.includes(q)) return false;
    }
    return true;
  });

  // Grouper par journée
  const designationsByRound = {};
  filteredDesignations.forEach(designation => {
    const match = matches.find(m => m.id === designation.match_id);
    const round = match ? match.round : 0;
    if (!designationsByRound[round]) {
      designationsByRound[round] = [];
    }
    designationsByRound[round].push(designation);
  });

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <h1 style={styles.title}>
        <FaUserTie style={styles.titleIcon} />
        {language === 'ar' ? 'إدارة تعيينات الحكام' : 'Gestion des Désignations d\'Arbitres'}
      </h1>

      {/* Formulaire */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>
          {formData.id ? (language === 'ar' ? 'تعديل تعيين' : 'Modifier une désignation') : (language === 'ar' ? 'إضافة تعyin' : 'Ajouter une désignation')}
        </h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{language === 'ar' ? 'المباراة *' : 'Match *'}</label>
            <select
              value={formData.match_id || ''}
              onChange={(e) => setFormData({ ...formData, match_id: e.target.value })}
              style={styles.input}
              required
            >
              <option value="">{language === 'ar' ? 'اختر مباراة' : 'Sélectionner un match'}</option>
              {matches.map(match => (
                <option key={match.id} value={match.id}>
                  J{match.round} - {match.home_team_name} vs {match.away_team_name} ({new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR')})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'الحكم الرئيسي *' : 'Arbitre Principal *'}</label>
              <input
                type="text"
                placeholder={language === 'ar' ? 'اسم الحكم الرئيسي' : 'Nom de l\'arbitre principal'}
                value={formData.main_referee || ''}
                onChange={(e) => setFormData({ ...formData, main_referee: e.target.value })}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>اسم الحكم الرئيسي (AR)</label>
              <input
                type="text"
                placeholder="اسم الحكم الرئيسي"
                value={formData.main_referee_ar || ''}
                onChange={(e) => setFormData({ ...formData, main_referee_ar: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'المساعد 1 *' : 'Assistant 1 *'}</label>
              <input
                type="text"
                placeholder={language === 'ar' ? 'اسم المساعد 1' : 'Nom de l\'assistant 1'}
                value={formData.assistant1 || ''}
                onChange={(e) => setFormData({ ...formData, assistant1: e.target.value })}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'المساعد 2 *' : 'Assistant 2 *'}</label>
              <input
                type="text"
                placeholder={language === 'ar' ? 'اسم المساعد 2' : 'Nom de l\'assistant 2'}
                value={formData.assistant2 || ''}
                onChange={(e) => setFormData({ ...formData, assistant2: e.target.value })}
                style={styles.input}
                required
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'الحكم الرابع' : '4ème Arbitre'}</label>
              <input
                type="text"
                placeholder={language === 'ar' ? 'اسم الحكم الرابع' : 'Nom du 4ème arbitre'}
                value={formData.fourth_official || ''}
                onChange={(e) => setFormData({ ...formData, fourth_official: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'المراقب' : 'Observateur'}</label>
              <input
                type="text"
                placeholder={language === 'ar' ? 'اسم المراقب' : 'Nom de l\'observateur'}
                value={formData.observer || ''}
                onChange={(e) => setFormData({ ...formData, observer: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formActions}>
            <button type="submit" style={styles.submitBtn}>
              <FaPlus style={styles.btnIcon} />
              {formData.id ? (language === 'ar' ? 'تعديل' : 'Modifier') : (language === 'ar' ? 'إضافة' : 'Ajouter')}
            </button>
            {formData.id && (
              <button type="button" onClick={() => setFormData({})} style={styles.cancelBtn}>
                {language === 'ar' ? 'إلغاء' : 'Annuler'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Liste des désignations */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            {language === 'ar' ? `قائمة التعيينات (${filteredDesignations.length})` : `Liste des désignations (${filteredDesignations.length})`}
          </h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder={language === 'ar' ? 'بحث باسم الفريق...' : 'Recherche par club...'}
              value={designationMatchFilter}
              onChange={(e) => setDesignationMatchFilter(e.target.value)}
              style={styles.filterInput}
            />
            <select
              value={selectedMatchFilter}
              onChange={(e) => setSelectedMatchFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">{language === 'ar' ? 'كل المباريات' : 'Tous les matchs'}</option>
              {matches.map(match => (
                <option key={match.id} value={match.id}>
                  J{match.round} - {match.home_team_name} vs {match.away_team_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {Object.keys(designationsByRound).length === 0 ? (
          <div style={styles.emptyState}>
            <FaUserTie style={styles.emptyIcon} />
            <p>{language === 'ar' ? 'لم يتم العثور على تعيينات' : 'Aucune désignation trouvée'}</p>
          </div>
        ) : (
          Object.keys(designationsByRound)
            .sort((a, b) => b - a)
            .map(round => (
              <div key={round} style={styles.roundSection}>
                <h3 style={styles.roundTitle}>
                  <FaFlag style={styles.roundIcon} />
                  {language === 'ar' ? `الجولة ${round}` : `Journée ${round}`}
                </h3>
                <div style={styles.designationsGrid}>
                  {designationsByRound[round].map(designation => (
                    <div key={designation.id} style={styles.designationCard}>
                      <div style={styles.matchInfo}>
                        <div style={styles.matchTitle}>
                          {getMatchInfo(designation.match_id)}
                        </div>
                      </div>

                      <div style={styles.refereesInfo}>
                        <div style={styles.refereeItem}>
                          <div style={styles.refereeRole}>
                            <FaUserTie style={styles.roleIcon} />
                            {language === 'ar' ? 'الحكم الرئيسي' : 'Arbitre Principal'}
                          </div>
                          <div style={styles.refereeName}>{designation.main_referee}</div>
                          {designation.main_referee_ar && (
                            <div style={styles.refereeNameAr}>{designation.main_referee_ar}</div>
                          )}
                        </div>

                        <div style={styles.assistantsRow}>
                          <div style={styles.assistantItem}>
                            <div style={styles.assistantRole}>
                              <FaFlag style={styles.roleIcon} />
                              {language === 'ar' ? 'المساعد 1' : 'Assistant 1'}
                            </div>
                            <div style={styles.assistantName}>{designation.assistant1}</div>
                          </div>
                          <div style={styles.assistantItem}>
                            <div style={styles.assistantRole}>
                              <FaFlag style={styles.roleIcon} />
                              {language === 'ar' ? 'المساعد 2' : 'Assistant 2'}
                            </div>
                            <div style={styles.assistantName}>{designation.assistant2}</div>
                          </div>
                        </div>

                        {(designation.fourth_official || designation.observer) && (
                          <div style={styles.othersRow}>
                            {designation.fourth_official && (
                              <div style={styles.otherItem}>
                                <span style={styles.otherLabel}>{language === 'ar' ? 'الحكم الرابع:' : '4ème Arbitre:'}</span>
                                <span style={styles.otherValue}>{designation.fourth_official}</span>
                              </div>
                            )}
                            {designation.observer && (
                              <div style={styles.otherItem}>
                                <span style={styles.otherLabel}>
                                  <FaEye style={styles.roleIcon} />
                                  {language === 'ar' ? 'المراقب:' : 'Observateur:'}
                                </span>
                                <span style={styles.otherValue}>{designation.observer}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={styles.cardActions}>
                        <button onClick={() => handleEdit(designation)} style={styles.editBtn}>
                          <FaEdit /> {language === 'ar' ? 'تعديل' : 'Modifier'}
                        </button>
                        <button onClick={() => handleDelete(designation.id)} style={styles.deleteBtn}>
                          <FaTrash /> {language === 'ar' ? 'حذف' : 'Supprimer'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

const styles = {
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '30px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    color: '#333'
  },
  titleIcon: {
    color: '#c41e3a',
    fontSize: '36px'
  },
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    marginBottom: '30px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#333'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333'
  },
  input: {
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    transition: 'border-color 0.3s'
  },
  filterInput: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '200px'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px'
  },
  formActions: {
    display: 'flex',
    gap: '10px'
  },
  submitBtn: {
    padding: '15px',
    background: '#c41e3a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '16px'
  },
  cancelBtn: {
    padding: '15px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  btnIcon: {
    fontSize: '14px'
  },
  filterSelect: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    minWidth: '250px'
  },
  roundSection: {
    marginBottom: '30px'
  },
  roundTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#c41e3a',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '10px'
  },
  roundIcon: {
    fontSize: '18px'
  },
  designationsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px'
  },
  designationCard: {
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    padding: '20px',
    background: '#fafafa',
    transition: 'all 0.3s'
  },
  matchInfo: {
    marginBottom: '15px',
    paddingBottom: '15px',
    borderBottom: '2px solid #e0e0e0'
  },
  matchTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  },
  refereesInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '15px'
  },
  refereeItem: {
    background: 'white',
    padding: '15px',
    borderRadius: '8px',
    border: '2px solid #c41e3a'
  },
  refereeRole: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#c41e3a',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  refereeName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  },
  refereeNameAr: {
    fontSize: '14px',
    color: '#666',
    marginTop: '4px'
  },
  assistantsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  assistantItem: {
    background: 'white',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  assistantRole: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#666',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  assistantName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  othersRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  otherItem: {
    background: 'white',
    padding: '10px',
    borderRadius: '6px',
    fontSize: '13px',
    display: 'flex',
    gap: '8px'
  },
  otherLabel: {
    fontWeight: 'bold',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  otherValue: {
    color: '#333'
  },
  roleIcon: {
    fontSize: '12px'
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '1px solid #e0e0e0'
  },
  editBtn: {
    padding: '8px 16px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1
  },
  deleteBtn: {
    padding: '8px 16px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#666'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #c41e3a',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
    opacity: 0.3
  },
  emptyHint: {
    fontSize: '14px',
    marginTop: '10px'
  }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .designationCard:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;
if (!document.getElementById('designations-section-styles')) {
  styleSheet.id = 'designations-section-styles';
  document.head.appendChild(styleSheet);
}

export default DesignationsSection;
